import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useReducer,
  useState,
  useTransition,
} from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Search from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  styled,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import BaseFilterButton from '../BaseFilterButton';
import Checkbox from '../Checkbox';
import NonIdealState from '../NonIdealState';
import SearchBar from '../SearchBar';
import { expandedValuesReducer } from './expandedValuesReducer';
import {
  flattenOptions,
  getDisplayableInputValue,
  isValueEqualToDefaultValue,
  labelHasSearchTerm,
  MULTI_SELECT_ITEM_HEIGHT,
} from './helpers';
import { MultiSelectFilterProps } from './types';
import { Option } from './types';
import VirtualizedListbox from './VirtualizedListbox';

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  defaultValue,
  fullWidth = false,
  icon,
  isMenuSearchable = true,
  isVirtualized = false,
  maxHeight = 480,
  menuTitle,
  menuWidth = 300,
  onChange,
  onClear,
  onClose,
  options,
  placeholder,
  simplifyLabel = false,
  value,
  variant = 'primary',
  xAlign = 'left',
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isPending, startTransition] = useTransition();
  const [expandedValues, expandedValuesDispatch] = useReducer(
    expandedValuesReducer,
    new Set<string>(),
  );

  const filteredOptions = useMemo(() => {
    const checkOptionForTerm = (option: Option): boolean => {
      const childHasSearchTerm =
        option.options && option.options.some(checkOptionForTerm);
      return childHasSearchTerm || labelHasSearchTerm(option, search);
    };
    return options.filter(checkOptionForTerm).map(option => {
      const optionChildren = labelHasSearchTerm(option, search)
        ? option.options
        : option.options?.filter(checkOptionForTerm);
      return {
        ...option,
        options: optionChildren,
      };
    });
  }, [search, options]);

  const selectedLabels = useMemo(() => {
    const renderTitle = (item: Option): string => {
      const itemOptionValues = flattenOptions(item.options ?? [])
        .filter(item => !item.options?.length)
        .map(item => item.value);

      const selectedLabel = value.includes(item.value) ? item.label : '';
      const hasSelectedChild = itemOptionValues.some(option =>
        value.includes(option),
      );

      if (!selectedLabel && !hasSelectedChild) {
        return '';
      }

      if (hasSelectedChild) {
        const childrenLabels =
          item?.options?.map(renderTitle).filter(Boolean).join(', ') ?? '';
        if (simplifyLabel) {
          return childrenLabels;
        }
        return `${item.label} (${childrenLabels})`;
      }

      return selectedLabel;
    };

    return options.map(renderTitle).filter(Boolean);
  }, [value, options, simplifyLabel]);

  const isOpen = Boolean(anchorEl);

  const handleOnClear = useCallback(() => {
    if (onClear) {
      onClear();
    }
    onChange(defaultValue ?? []);
  }, [defaultValue, onClear, onChange]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setAnchorEl(null);
    expandedValuesDispatch({ type: 'RESET' });
    setSearch('');
  }, [onClose]);

  const handleMenuItemClick = useCallback(
    (item: Option, e?: React.MouseEvent) => {
      // Prevent handling if click was on checkbox or "Only" button
      if (e?.target instanceof HTMLInputElement) {
        return;
      }

      if (item.options?.length) {
        startTransition(() => {
          expandedValuesDispatch({ type: 'TOGGLE', value: item.value });
        });
      } else {
        onChange(
          value.includes(item.value)
            ? value.filter(v => v !== item.value)
            : [...value, item.value],
        );
      }
    },
    [onChange, value],
  );

  const noSearchResults = filteredOptions.length === 0;

  const handleCheckboxClick = useCallback(
    (item: Option, e: ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      const hasOptions = Boolean(item.options);
      const itemOptionValues = flattenOptions(item.options ?? [])
        .filter(child => !child.options?.length)
        .map(child => child.value);
      const isChecked = hasOptions
        ? itemOptionValues.every(option => value.includes(option))
        : value.includes(item.value);

      // If unchecked and not expanded, expand
      if (!isChecked && hasOptions && !expandedValues.has(item.value)) {
        expandedValuesDispatch({ type: 'TOGGLE', value: item.value });
      }

      if (hasOptions) {
        if (isChecked) {
          onChange(value.filter(v => !itemOptionValues.includes(v)));
        } else {
          onChange([
            ...value,
            ...itemOptionValues.filter(v => !value.includes(v)),
          ]);
        }
      } else {
        onChange(
          isChecked
            ? value.filter(v => v !== item.value)
            : [...value, item.value],
        );
      }
    },
    [value, onChange, expandedValues],
  );

  const renderListOption = useCallback(
    (item: Option) => {
      const hasOptions = Boolean(item.options);
      const itemOptionValues = flattenOptions(item.options ?? [])
        .filter(child => !child.options?.length)
        .map(child => child.value);
      const isChecked = hasOptions
        ? itemOptionValues.every(option => value.includes(option))
        : value.includes(item.value);
      const isIndeterminate =
        !isChecked && itemOptionValues.some(option => value.includes(option));
      const isExpanded = expandedValues.has(item.value);
      const key = item.label + item.value;

      return (
        <div style={{ width: '100%' }} key={key}>
          <MenuItem
            key={item.label}
            onClick={e => handleMenuItemClick(item, e)}
          >
            <Checkbox
              indeterminate={isIndeterminate}
              padding='4px 12px'
              aria-label={item.label}
              checked={isChecked}
              onChange={e => handleCheckboxClick(item, e)}
            />
            <ListItemText
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              primary={
                <Typography variant='font14' noWrap>
                  {item.optionStartAdornment ? (
                    <Box display='flex' alignItems='center'>
                      <Box
                        mr='5px'
                        display='flex'
                        alignItems='center'
                        color={theme.palette.colors.grey[800]}
                      >
                        {item.optionStartAdornment}
                      </Box>
                      {item.label}
                    </Box>
                  ) : (
                    item.label
                  )}
                </Typography>
              }
            />
            {hasOptions ? (
              <IconButton
                sx={{
                  transform: `rotate(${isExpanded ? '180' : '0'}deg)`,
                }}
                onClick={e => {
                  e.stopPropagation();
                  handleMenuItemClick(item);
                }}
              >
                <KeyboardArrowDown />
              </IconButton>
            ) : (
              <OnlyButton
                onClick={e => {
                  e.stopPropagation();
                  onChange([item.value]);
                }}
              >
                <Typography
                  color={theme.palette.colors.purple[500]}
                  variant='font12'
                >
                  Only
                </Typography>
              </OnlyButton>
            )}
          </MenuItem>
          {!isVirtualized && hasOptions && isExpanded && (
            <Box marginLeft={2}>
              {item.options?.map(option => renderListOption(option))}
            </Box>
          )}
        </div>
      );
    },
    [
      expandedValues,
      value,
      onChange,
      handleMenuItemClick,
      handleCheckboxClick,
      theme,
      isVirtualized,
    ],
  );

  // Add loading indicator for transitions
  const LoadingOverlay = isPending ? (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 1,
      }}
    >
      <CircularProgress size={20} />
    </Box>
  ) : null;

  return (
    <>
      <BaseFilterButton
        value={getDisplayableInputValue({
          placeholder,
          selectedLabels,
          value,
          variant,
        })}
        isOpen={isOpen}
        showCaret={
          isValueEqualToDefaultValue(value, defaultValue) || !value.length
        }
        onClick={e => setAnchorEl(e.currentTarget)}
        onClear={handleOnClear}
        startAdornment={icon}
        aria-label='multi select filter button'
        size={fullWidth ? 'medium' : 'small'}
        overflowTooltipContent={selectedLabels.join(', ')}
      />
      <Menu
        slotProps={{
          paper: {
            elevation: 2,
            sx: {
              border: `1px solid ${theme.palette.colors.grey[500]}`,
              maxHeight: maxHeight,
              width: menuWidth,
            },
          },
        }}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        open={isOpen}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          sx: {
            paddingBottom: 0,
            paddingTop: menuTitle ? 0 : 1,
          },
          variant: menuTitle ? 'menu' : 'selectedMenu',
        }}
        anchorOrigin={{
          horizontal: xAlign,
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: xAlign,
          vertical: 'top',
        }}
        sx={{
          marginTop: '8px',
        }}
      >
        {menuTitle && (
          <Box padding='12px 16px' component='div' tabIndex={undefined}>
            <Typography variant='font16Bold' color={theme.palette.colors.black}>
              {menuTitle}
            </Typography>
          </Box>
        )}
        {isMenuSearchable && (
          <SearchBarContainer>
            <SearchBar
              size='small'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchBarContainer>
        )}
        {LoadingOverlay}
        <ItemContainer>
          {noSearchResults ? (
            <NoResultsContainer>
              <NonIdealState icon={<Search />} title='No results found' />
            </NoResultsContainer>
          ) : isVirtualized ? (
            <VirtualizedListbox
              options={filteredOptions}
              expandedValues={expandedValues}
              renderOption={renderListOption}
              maxHeight={maxHeight}
            />
          ) : (
            filteredOptions.map(renderListOption)
          )}
        </ItemContainer>
      </Menu>
    </>
  );
};

export default MultiSelectFilter;

const SearchBarContainer = styled('div')`
  padding: 0 12px 12px 12px;
`;

const ItemContainer = styled('div')`
  width: 100%;
  flex: 1;
  margin-top: 5px;
`;

const OnlyButton = styled('button')`
  display: none;
  background: transparent;
  outline: none;
  border: none;
  border-radius: 4px;
  padding: 2px 4px 2px 4px;
  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.palette.colors.purple[200]};
  }
`;

const MenuItem = styled('div')`
  padding: 2px 16px;
  display: flex;
  height: ${MULTI_SELECT_ITEM_HEIGHT}px;
  margin-bottom: 4px;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: ${props => props.theme.palette.colors.purple[100]};
    cursor: pointer;
    button {
      display: flex;
    }
  }
`;

const NoResultsContainer = styled('div')`
  height: 174px;
`;
